import { memo, useMemo } from "react";
import { Collapse, Flex, Typography } from "antd";
import { HeartOutlined, HomeOutlined, GlobalOutlined } from "@ant-design/icons";

import useLocalStore from "@/hooks/useLocalStore";
import T from "@/components/common/T";
import useJoinWalksDetails from "./useJoinWalksDetails";
import PanelHeader from "./PanelHeader";
import SectionGrid from "./SectionGrid";
import ThemeCard from "./ThemeCard";
import WalkCard from "./WalkCard";
import CreateWalkBanner from "./CreateWalkBanner";
import STYLES from "./styles";

const { Title, Text } = Typography;

const THEME_COL = { xl: 6, lg: 6, md: 8, sm: 12, xs: 24 };
const WALK_COL = { xs: 24, sm: 12, lg: 6 };

// Maps the detected geo-IP location onto the create-walk form field keys.
const locationToFormValues = (loc) =>
    loc
        ? {
            location: [loc.city, loc.state, loc.country].filter(Boolean).join(", "),
            country_name: loc.country ?? "",
            state_name: loc.state ?? "",
            city_name: loc.city ?? "",
            latitude: loc.latitude ?? "",
            longitude: loc.longitude ?? "",
        }
        : {};

const JoinWalkPage = () => {
    const setLocalStates = useLocalStore((s) => s.setStates);

    const { data, isLoading } = useJoinWalksDetails();

    const location = data?.location ?? null;
    const themes = data?.themes ?? [];
    const cityWalks = data?.cityWalks ?? [];
    const globalCountryWalks = data?.globalCountryWalks ?? [];

    // Opens the create-walk modal, prefilling the detected location and any
    // extra values passed by the caller (e.g. the selected theme).
    const openCreateWalk = (initialValues = null) => {
        const merged = { ...locationToFormValues(location), ...(initialValues || {}) };
        setLocalStates({
            createWalkModal: { open: true, initialValues: Object.keys(merged).length ? merged : null },
        });
    };

    const accordionItems = useMemo(() => [
        {
            key: "reasons",
            className: "jw-panel",
            label: <PanelHeader icon={<HeartOutlined />} title={<T k="What will you walk for?" />} count={themes.length} />,
            children: (
                <>
                    <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                        <T k="What matters so much to you that you refuse to give up on it? Select one reason and walk for it wherever you are." />
                    </Text>
                    <div className="jw-section-scroll">
                        <SectionGrid
                            items={themes}
                            isLoading={isLoading}
                            emptyText={<T k="No themes available." />}
                            colProps={THEME_COL}
                            renderItem={(theme) => <ThemeCard theme={theme} onCreate={openCreateWalk} />}
                        />
                    </div>
                </>
            ),
        },
        {
            key: "city",
            className: "jw-panel",
            label: <PanelHeader icon={<HomeOutlined />} title={<T k="City walks near you" />} count={cityWalks.length} />,
            children: (
                <div className="jw-section-scroll">
                    <SectionGrid
                        items={cityWalks}
                        isLoading={isLoading}
                        emptyText={<T k="No city walks yet." />}
                        colProps={WALK_COL}
                        renderItem={(walk) => <WalkCard walk={walk} />}
                    />
                </div>
            ),
        },
        {
            key: "global",
            className: "jw-panel",
            label: <PanelHeader icon={<GlobalOutlined />} title={<T k="Global & country walks" />} count={globalCountryWalks.length} />,
            children: (
                <div className="jw-section-scroll">
                    <SectionGrid
                        items={globalCountryWalks}
                        isLoading={isLoading}
                        emptyText={<T k="No global or country walks yet." />}
                        colProps={WALK_COL}
                        renderItem={(walk) => <WalkCard walk={walk} />}
                    />
                </div>
            ),
        },
    ], [themes, cityWalks, globalCountryWalks, isLoading]);

    return (
        <div style={{ padding: 24, background: "#f6f7f9" }}>
            <style>{STYLES}</style>

            {/* Page intro */}
            <Flex vertical gap={4} style={{ marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}><T k="Join a Walk" /></Title>
                <Text type="secondary" style={{ fontSize: 15 }}>
                    <T k="Pick a reason, a city, or a global walk — and put your pin on the map." />
                </Text>
            </Flex>

            {/* Sections 1–3 */}
            <Collapse
                className="jw-collapse"
                defaultActiveKey={["reasons", "city", "global"]}
                bordered={false}
                style={{ background: "transparent", marginBottom: 32 }}
                items={accordionItems}
            />

            {/* Section 4 — create-a-walk call to action */}
            <CreateWalkBanner onCreate={openCreateWalk} />
        </div>
    );
};

export default memo(JoinWalkPage);
